import {Avatar, ComboBox, DeprecatedCreativeModal as Modal, FlexRow, Label} from '@forecast-it/design-system';
import {useIntl} from 'react-intl';
import React, {useMemo} from 'react';
import {ReportRow} from '../../ReportTypes';
import {createFragmentContainer, graphql} from 'react-relay';
import {getNotNullishNodes} from '../../../../shared/util/NotNullPredicate';
import type {TransferOwnershipModal_viewer$data as TransferOwnershipModal_viewer} from './__generated__/TransferOwnershipModal_viewer.graphql';
import {profilePicRectSrc} from '../../../../../directApi';
import {newOwnerType} from './TransferOwnership';

type TransferOwnershipModalProps = {
	closeModal: () => void;
	transferOwnership: (newOwner: newOwnerType) => void;
	report: ReportRow;
	viewer: TransferOwnershipModal_viewer;
};

function isSameOwner(newOwnerId: string, report: ReportRow) {
	return newOwnerId === report?.person?.id;
}

const TransferOwnershipModal = ({
	closeModal,
	transferOwnership,
	report,
	viewer,
}: TransferOwnershipModalProps): React.JSX.Element => {
	const {formatMessage} = useIntl();
	const [newOwnerId, setNewOwnerId] = React.useState(report?.person?.id);
	const persons = getNotNullishNodes(viewer?.company?.allPersons?.edges);

	const defaultItems = useMemo(() => {
		return persons.map(person => ({
			avatar: person.profilePictureId ? profilePicRectSrc(person.profilePictureId) : undefined,
			id: person.id,
			name: person.fullName,
		}));
	}, [persons]);

	const handleTransferOwnership = (): void => {
		const newOwner = persons.find(person => newOwnerId === person.id);
		if (newOwner) {
			transferOwnership(newOwner);
			closeModal();
		}
	};

	return (
		<Modal
			title={formatMessage({id: 'common.action.transfer_ownership'})}
			creativeButtonText={formatMessage({id: 'common.action.transfer_ownership'})}
			onCreateHandler={handleTransferOwnership}
			disabled={!newOwnerId || isSameOwner(newOwnerId, report)}
			closeModal={closeModal}
			replicateDesignSystem
			data-cy="transfer-ownership-modal"
		>
			<ComboBox
				defaultItems={defaultItems}
				defaultSelectedKey={newOwnerId}
				label="Select new owner"
				onChange={item => setNewOwnerId(item)}
				data-cy="transfer-ownership-combobox"
			>
				{item => (
					<ComboBox.Item textValue={item.name || ''}>
						<FlexRow>
							<Avatar src={item.avatar} size={'s'} name={item.name || ''} />
							<Label>{item.name}</Label>
						</FlexRow>
					</ComboBox.Item>
				)}
			</ComboBox>
		</Modal>
	);
};

export const TransferOwnershipModalQuery = graphql`
	query TransferOwnershipModalQuery($permissions: [String!]!) {
		viewer {
			actualPersonId
			component(name: "transfer_ownership_modal")
			...TransferOwnershipModal_viewer @arguments(permissions: $permissions)
		}
	}
`;

export default createFragmentContainer(TransferOwnershipModal, {
	viewer: graphql`
		fragment TransferOwnershipModal_viewer on Viewer @argumentDefinitions(permissions: {type: "[String!]!"}) {
			company {
				allPersons(first: 10000, permissions: $permissions) @connection(key: "Company_allPersons") {
					edges {
						node {
							id
							fullName
							profilePictureId
						}
					}
				}
			}
		}
	`,
});
